import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { PageHeader } from './PageHeader';

export interface HubTab {
  key: string;
  label: string;
  count?: number;
  tone?: 'warn' | 'error';
  render: () => ReactNode;
}

// Hub: ein Arbeitsbereich mit Reitern statt vieler Menüpunkte. Der aktive Reiter steht in der URL (?ansicht=…),
// damit Links aus Startseite und Benachrichtigungen direkt auf die richtige Ansicht zeigen. Beim Wechsel bleiben
// nur die bereichsweiten Filter (keep) erhalten, reiterspezifische Parameter werden zurückgesetzt.
export function Hub({
  title,
  description,
  tabs,
  actions,
  filters,
  keep = [],
  label,
}: {
  title: string;
  description?: ReactNode;
  tabs: HubTab[];
  actions?: ReactNode;
  filters?: ReactNode;
  keep?: string[];
  label?: string;
}) {
  const [params, setParams] = useSearchParams();
  const active = tabs.find((t) => t.key === params.get('ansicht')) ?? tabs[0];
  const select = (key: string) => {
    const next = new URLSearchParams();
    for (const k of keep) {
      const v = params.get(k);
      if (v) next.set(k, v);
    }
    next.set('ansicht', key);
    setParams(next);
  };
  if (!active) return null;
  return (
    <div className="page hub">
      <PageHeader title={title} description={description} actions={actions} />
      {filters}
      {tabs.length > 1 && (
        <div className="tabs" role="tablist" aria-label={label ?? title}>
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t === active}
              className={t === active ? 'tab active' : 'tab'}
              onClick={() => select(t.key)}
            >
              {t.label}
              {t.count != null && (
                <span className={t.tone && t.count > 0 ? `tab-count tab-count-${t.tone}` : 'tab-count'} aria-label={`${t.count} offen`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      <div role="tabpanel" aria-label={active.label} className="hub-panel">
        {active.render()}
      </div>
    </div>
  );
}
